import { Popover } from "@base-ui/react"
import { useIsDesktop } from "@src/hooks/useMediaQuery"
import { usePortalContainer } from "@src/hooks/usePortalContainer"
import { ArrowIcon } from "@src/icons"
import clsx from "clsx"
import type { ComponentProps, ComponentType, MouseEventHandler } from "react"
import Button from "./Button"

type ActionPopoverItem = {
  label: string
  icon?: ComponentType<{ className?: string }>
  href?: string
  onClick?: MouseEventHandler<HTMLElement>
}

export type ActionPopoverProps = {
  trigger: ComponentProps<typeof Popover.Trigger>["render"]
  items: ActionPopoverItem[]
  open?: ComponentProps<typeof Popover.Root>["open"]
  onOpenChange?: ComponentProps<typeof Popover.Root>["onOpenChange"]
  showArrow?: boolean
  align?: ComponentProps<typeof Popover.Positioner>["align"]
  sideOffset?: ComponentProps<typeof Popover.Positioner>["sideOffset"]
}

export function ActionPopover({
  trigger,
  items,
  open,
  onOpenChange,
  showArrow = false,
  align = "end",
  sideOffset = 4,
}: ActionPopoverProps) {
  const portalContainer = usePortalContainer()
  const isDesktop = useIsDesktop()

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger render={trigger} />

      <Popover.Portal container={portalContainer}>
        <Popover.Positioner
          className="z-10 outline-hidden"
          collisionPadding={
            isDesktop
              ? 8
              : {
                  top: 8,
                  right: 8,
                  bottom: 64,
                  left: 8,
                }
          }
          sideOffset={sideOffset}
          align={align}
          arrowPadding={14}
        >
          <Popover.Popup
            className={clsx(
              "flex max-w-(--available-width) origin-(--transform-origin) flex-col gap-1 rounded-2xl bg-gray-900 p-1 shadow-lg",
              "transition duration-100 ease-out",
              "dark:-outline-offset-1 dark:shadow-none dark:outline-1 dark:outline-white/5",
              "data-starting-style:-translate-y-1 data-starting-style:scale-95 data-starting-style:opacity-0",
              "data-ending-style:-translate-y-1 data-ending-style:scale-95 data-ending-style:opacity-0 data-ending-style:duration-75 data-ending-style:ease-in"
            )}
          >
            {showArrow && (
              <Popover.Arrow className="data-[side=bottom]:-top-2 data-[side=top]:-bottom-2 text-gray-900 data-[side=top]:rotate-180">
                <ArrowIcon className="h-2.5 w-5 shrink-0" />
              </Popover.Arrow>
            )}

            {items.map(({ label, icon: Icon, href, onClick }) => {
              if (!href && !onClick) {
                return (
                  <div className="flex h-10 shrink-0 items-center justify-start rounded-xl px-4 font-bold text-sm text-white leading-none tracking-tight">
                    {Icon && <Icon className="mr-1 size-4.5 shrink-0" />}
                    {label}
                  </div>
                )
              }

              return (
                <Button
                  key={label}
                  variant="dark"
                  align="start"
                  size="lg"
                  href={href}
                  onClick={onClick}
                  {...(href && !href.startsWith("/")
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                >
                  {Icon && <Icon className="mr-1 size-4.5 shrink-0" />}
                  {label}
                </Button>
              )
            })}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  )
}
